import { useShallow } from "zustand/shallow";
import clsx from "clsx";
import { Switch } from "@heroui/react";

import ShareButton from "@/shared/components/common/ShareButton";
import { useBoundStore } from "@/app/store";
import { getHost } from "@/shared/utils/device";
import { uuidv4 } from "@/shared/utils/uuidv4";

interface ShareNoteOptionProps {
  className?: string;
  noteId: number;
}

export function ShareNoteOption({ className, noteId }: ShareNoteOptionProps) {
  const { notes, updateNote } = useBoundStore(
    useShallow((state) => ({ notes: state.notes.main, updateNote: state.notes.updateNote }))
  );
  const selectedNote = notes?.find((note) => note.id === noteId);
  const noteLink = `${getHost()}/share/notes/${selectedNote?.public_uuid}`;
  const isSelected = Boolean(selectedNote?.public_uuid);

  const handleToggleShare = async () => {
    // If the note is already shared, remove sharing
    if (selectedNote?.public_uuid && noteId) {
      updateNote(
        noteId,
        { text_content: selectedNote.text_content, html_content: selectedNote.html_content, public_uuid: null },
        false
      );
      return;
    }

    // If the note is not shared, generate a new UUID and share it
    if (noteId) {
      const uuid = uuidv4();
      updateNote(
        noteId,
        { public_uuid: uuid, text_content: selectedNote?.text_content, html_content: selectedNote?.html_content },
        true
      );
    }
  };

  return (
    <div className={clsx("min-w-48 p-1 sm:max-w-80", className)}>
      <Switch
        autoFocus
        color="primary"
        classNames={{
          base: "flex-row-reverse max-w-full w-full justify-between",
          label: "m-0",
          thumb: clsx({ "dark:bg-black": isSelected }),
        }}
        isSelected={isSelected}
        size="sm"
        onValueChange={handleToggleShare}
      >
        Share note
      </Switch>

      {selectedNote?.public_uuid ? (
        <div className="bg-content2 mt-6 flex items-center justify-between gap-2 rounded-lg p-2 select-text">
          <span className="text-sm break-all">{noteLink}</span>
          <ShareButton data={{ text: noteLink }} size="sm" />
        </div>
      ) : null}
    </div>
  );
}
